import * as z from 'zod';
import { childIdSchema, deviceIdSchema, familyIdSchema } from '../primitives/id.js';
import { instantSchema } from '../primitives/time.js';
export const devicePlatformSchema = z.enum(['ANDROID', 'IOS']);
export const deviceSchema = z.object({
    id: deviceIdSchema,
    familyId: familyIdSchema,
    name: z.string().min(1).max(60),
    platform: devicePlatformSchema,
    /**
     * Which children this device may run, and which is loaded now.
     *
     * Many-to-many because a shared family tablet is normal. Switching the active
     * profile passes the Parent Gate, since otherwise a child moves to a sibling's
     * profile and inherits their budget (SPEC.md §19.1).
     */
    assignedChildIds: z.array(childIdSchema),
    activeChildId: childIdSchema.nullable(),
    isOnline: z.boolean(),
    lastSeenAt: instantSchema.nullable(),
    createdAt: instantSchema,
});
/**
 * A pairing code shown by an unpaired device.
 *
 * Single use and short-lived. It is displayed on a screen a stranger could
 * photograph, so it must be worth nothing a few minutes later.
 */
export const pairingCodeSchema = z
    .string()
    .regex(/^[A-Z0-9]{8}$/, 'Expected eight uppercase characters');
export const pairingStatusSchema = z.enum(['PENDING', 'CLAIMED', 'EXPIRED']);
/**
 * What the pairing QR code actually encodes.
 *
 * A bare code would scan into a string the parent app cannot recognise as ours,
 * and a scanner has no way to tell it apart from any other eight characters in
 * a square. The URI is self-identifying and leaves room for a deep link later.
 *
 * ```text
 * kidshub://pair?v=1&code=BQKH4FM3
 * ```
 *
 * Defined here rather than in either application because both ends must agree:
 * the Kids App renders it, the Parent App reads it. Two implementations of one
 * format is exactly the duplication this package exists to prevent.
 *
 * The version is present so that a future format change can be recognised
 * rather than silently mis-parsed by an older build — an installed tablet may be
 * months behind the phone scanning it.
 */
export const PAIRING_URI_SCHEME = 'kidshub';
export const PAIRING_URI_ACTION = 'pair';
export const PAIRING_URI_VERSION = '1';
export const buildPairingUri = (code) => `${PAIRING_URI_SCHEME}://pair?v=${PAIRING_URI_VERSION}&code=${code}`;
/**
 * Removes what a person adds and a scanner does not.
 *
 * The tablet groups the code so it can be read aloud (`K74M·29XQ`), so a parent
 * copying what they see reproduces the separator. Spaces and dashes arrive
 * whether or not they were ever displayed, and keyboards default to lower case.
 */
const normaliseCode = (value) => value.replace(/[\s·.-]/g, '').toUpperCase();
const asCode = (raw) => {
    const parsed = pairingCodeSchema.safeParse(normaliseCode(raw));
    return parsed.success ? { status: 'OK', pairingCode: parsed.data } : { status: 'INVALID' };
};
/**
 * Reads a scanned QR payload, or a code a parent typed in by hand.
 *
 * Accepts both on purpose: the camera fails to focus in a dim room often enough
 * that manual entry is a real path, not a fallback nobody uses.
 *
 * Parsed by hand rather than through `URL`, whose `searchParams` has not been
 * dependable across Hermes builds — this package runs on a tablet as well as in
 * a browser, and a format this small does not justify a polyfill.
 */
export const parsePairingUri = (value) => {
    const trimmed = value.trim();
    if (trimmed === '') {
        return { status: 'INVALID' };
    }
    if (!trimmed.toLowerCase().startsWith(`${PAIRING_URI_SCHEME}:`)) {
        // Not a URI at all: read it as a code taken off the tablet screen.
        return asCode(trimmed);
    }
    const withoutScheme = trimmed.slice(`${PAIRING_URI_SCHEME}:`.length).replace(/^\/+/, '');
    const [target = '', rawQuery = ''] = splitOnce(withoutScheme, '?');
    if (target.replace(/\/+$/, '').toLowerCase() !== PAIRING_URI_ACTION) {
        return { status: 'INVALID' };
    }
    const query = new Map();
    for (const pair of rawQuery.split('&')) {
        const [key, parameter] = splitOnce(pair, '=');
        if (key !== undefined && parameter !== undefined) {
            query.set(key, parameter);
        }
    }
    const version = query.get('v');
    // A missing version is the original format, which this build still reads.
    // A version it does not know is a newer tablet, and worth saying so.
    if (version !== undefined && version !== PAIRING_URI_VERSION) {
        return { status: 'UNSUPPORTED_VERSION', version };
    }
    const code = query.get('code');
    return code === undefined ? { status: 'INVALID' } : asCode(code);
};
const splitOnce = (value, separator) => {
    const at = value.indexOf(separator);
    return at === -1 ? [value] : [value.slice(0, at), value.slice(at + separator.length)];
};
//# sourceMappingURL=device.js.map